/**
 * FloorPlanModals.jsx
 * Presentational modal dialogs for the Floor Plan Designer.
 * Each modal is a self-contained component that receives only the props it needs.
 * Extracted from FloorPlanDesignerPage.jsx to keep the main page file manageable.
 */

import { useState } from 'react';
import { MdClose, MdSearch, MdKeyboard, MdSave } from 'react-icons/md';

// ── Settings Modal ────────────────────────────────────────────────────────────

/**
 * Modal for editing floorplan-level settings: scale, canvas dimensions, and grid.
 *
 * @param {Object}   props
 * @param {Object}   props.settingsForm        - Current settings form values.
 * @param {Function} props.onSettingsChange    - Called with updated settingsForm object on each field change.
 * @param {boolean}  props.showWatermark       - Whether the floor-below watermark overlay is enabled.
 * @param {Function} props.onWatermarkChange   - Called with the new boolean value when the checkbox changes.
 * @param {Function} props.onSave              - Called when the user confirms the changes.
 * @param {Function} props.onClose             - Called when the modal should be dismissed.
 */
export const SettingsModal = ({
    settingsForm,
    onSettingsChange,
    showWatermark,
    onWatermarkChange,
    onSave,
    onClose
}) => (
    <div className="fp-modal-overlay" onClick={onClose}>
        <div className="fp-modal" onClick={e => e.stopPropagation()}>
            <div className="fp-modal-header">
                <h2>Floorplan Settings</h2>
                <button className="btn-icon" onClick={onClose}><MdClose /></button>
            </div>
            <div className="fp-modal-body">
                <label>
                    Scale (meters per pixel):
                    <input
                        type="number"
                        step="0.001"
                        min="0.001"
                        value={settingsForm.metersPerPixel}
                        onChange={e => onSettingsChange({
                            ...settingsForm,
                            metersPerPixel: parseFloat(e.target.value) || 0.05
                        })}
                    />
                    <small>e.g., 0.05 = 20 pixels per meter. A 100m wall = 2000px.</small>
                </label>
                <label>
                    Canvas Width (m):
                    <input
                        type="number"
                        step="0.1"
                        min="1"
                        value={parseFloat((settingsForm.canvasWidth * settingsForm.metersPerPixel).toFixed(2))}
                        onChange={e => {
                            const meters = parseFloat(e.target.value) || 1;
                            onSettingsChange({
                                ...settingsForm,
                                canvasWidth: Math.round(meters / settingsForm.metersPerPixel)
                            });
                        }}
                    />
                    <small>Canvas width in meters ({settingsForm.canvasWidth}px at current scale).</small>
                </label>
                <label>
                    Canvas Height (m):
                    <input
                        type="number"
                        step="0.1"
                        min="1"
                        value={parseFloat((settingsForm.canvasHeight * settingsForm.metersPerPixel).toFixed(2))}
                        onChange={e => {
                            const meters = parseFloat(e.target.value) || 1;
                            onSettingsChange({
                                ...settingsForm,
                                canvasHeight: Math.round(meters / settingsForm.metersPerPixel)
                            });
                        }}
                    />
                    <small>Canvas height in meters ({settingsForm.canvasHeight}px at current scale).</small>
                </label>
                <label>
                    Grid Spacing (m):
                    <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={parseFloat((settingsForm.gridSpacing * settingsForm.metersPerPixel).toFixed(2))}
                        onChange={e => {
                            const meters = parseFloat(e.target.value) || 0;
                            onSettingsChange({
                                ...settingsForm,
                                gridSpacing: Math.round(meters / settingsForm.metersPerPixel)
                            });
                        }}
                    />
                    <small>
                        0 = no grid. Recommended: 1m
                        (= {Math.round(1 / settingsForm.metersPerPixel)}px per grid cell).
                    </small>
                </label>
                <label>
                    Show floor watermark:
                    <input
                        type="checkbox"
                        checked={showWatermark}
                        onChange={e => onWatermarkChange(e.target.checked)}
                    />
                    <small>Shows a faint outline of the floor below for alignment.</small>
                </label>
            </div>
            <div className="fp-modal-footer">
                <button className="btn-secondary" onClick={onClose}>Cancel</button>
                <button className="btn-primary" onClick={onSave}>Save Settings</button>
            </div>
        </div>
    </div>
);

// ── Floor Add / Edit Modal ────────────────────────────────────────────────────

/**
 * Modal for adding a new floor or editing an existing floor's name and number.
 *
 * @param {Object}   props
 * @param {'add'|'edit'} props.mode          - Determines the modal title and confirm button label.
 * @param {Object}   props.floorForm         - Current floor form values { name, floorNumber }.
 * @param {Function} props.onFloorFormChange - Called with updated floorForm object on each field change.
 * @param {Function} props.onConfirm         - Called when the user clicks the Add/Update button.
 * @param {Function} props.onClose           - Called when the modal should be dismissed.
 */
export const FloorModal = ({
    mode,
    floorForm,
    onFloorFormChange,
    onConfirm,
    onClose
}) => (
    <div className="fp-modal-overlay" onClick={onClose}>
        <div className="fp-modal" onClick={e => e.stopPropagation()}>
            <div className="fp-modal-header">
                <h2>{mode === 'add' ? 'Add Floor' : 'Edit Floor'}</h2>
                <button className="btn-icon" onClick={onClose}><MdClose /></button>
            </div>
            <div className="fp-modal-body">
                <label>
                    Floor Name:
                    <input
                        type="text"
                        value={floorForm.name}
                        onChange={e => onFloorFormChange({ ...floorForm, name: e.target.value })}
                        placeholder="e.g., Ground Floor, Upper Level, Basement"
                    />
                </label>
                <label>
                    Floor Number:
                    <input
                        type="number"
                        value={floorForm.floorNumber}
                        onChange={e => onFloorFormChange({
                            ...floorForm,
                            floorNumber: parseInt(e.target.value) || 0
                        })}
                    />
                    <small>0 = Ground, 1 = First Floor, -1 = Basement, etc.</small>
                </label>
            </div>
            <div className="fp-modal-footer">
                <button className="btn-secondary" onClick={onClose}>Cancel</button>
                <button className="btn-primary" onClick={onConfirm}>
                    {mode === 'add' ? 'Add Floor' : 'Update Floor'}
                </button>
            </div>
        </div>
    </div>
);

// ── Link Record Modal ─────────────────────────────────────────────────────────

/**
 * Modal for linking a canvas element to a real CapTrac / MDA data record
 * (Rentable Unit, Promotional Area, or Media Space).
 *
 * @param {Object}   props
 * @param {Object}   props.selectedElement     - The element currently selected on the canvas.
 * @param {Array}    props.rentableUnits       - Available rentable unit records.
 * @param {Array}    props.promotionalAreas    - Available promotional area records.
 * @param {Array}    props.mediaSpaces         - Available media space records.
 * @param {string}   props.searchTerm          - Current value of the search filter input.
 * @param {Function} props.onSearchChange      - Called with the new search string on each keypress.
 * @param {Function} props.onLinkRecord        - Called with (type, recordId) when a record is selected.
 * @param {Function} props.onClose             - Called when the modal should be dismissed.
 */
export const LinkRecordModal = ({
    selectedElement,
    rentableUnits,
    promotionalAreas,
    mediaSpaces,
    searchTerm,
    onSearchChange,
    onLinkRecord,
    onClose
}) => {
    const handleClose = () => {
        onSearchChange('');
        onClose();
    };

    return (
        <div className="fp-modal-overlay" onClick={handleClose}>
            <div className="fp-modal fp-modal-wide" onClick={e => e.stopPropagation()}>
                <div className="fp-modal-header">
                    <h2>Link to Record</h2>
                    <button className="btn-icon" onClick={handleClose}><MdClose /></button>
                </div>
                <div className="fp-modal-body">
                    <div className="link-search">
                        <MdSearch size={18} />
                        <input
                            type="text"
                            placeholder="Search records..."
                            value={searchTerm}
                            onChange={e => onSearchChange(e.target.value)}
                            autoFocus
                        />
                    </div>

                    {/* Rentable Units */}
                    {selectedElement?.elementType === 'RentableUnit' && (
                        <>
                            <h3>Rentable Units</h3>
                            <div className="link-list">
                                {rentableUnits
                                    .filter(unit => !searchTerm ||
                                        (unit.propertyRentableUnitNo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        (unit.rudescription || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        (unit.nextTenantName || '').toLowerCase().includes(searchTerm.toLowerCase())
                                    )
                                    .map(unit => (
                                        <div
                                            key={unit.propertyRentableUnitID}
                                            className="link-item"
                                            onClick={() => {
                                                onLinkRecord('rentableUnit', unit.propertyRentableUnitID);
                                                onSearchChange('');
                                            }}
                                        >
                                            <strong>{unit.propertyRentableUnitNo}</strong>
                                            <span>{unit.rudescription || 'No description'}</span>
                                            <span className="link-detail">
                                                {unit.area ? `${unit.area} m²` : ''}
                                            </span>
                                            {unit.nextTenantName && (
                                                <span className="link-tenant">
                                                    Tenant: {unit.nextTenantName}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                {rentableUnits.length === 0 && (
                                    <p className="empty-msg">No rentable units found for this property.</p>
                                )}
                            </div>
                        </>
                    )}

                    {/* Promotional Areas */}
                    {selectedElement?.elementType === 'PromotionalArea' && (
                        <>
                            <h3>Promotional Areas</h3>
                            <div className="link-list">
                                {promotionalAreas
                                    .filter(area => !searchTerm ||
                                        (area.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        (area.locationDescription || '').toLowerCase().includes(searchTerm.toLowerCase())
                                    )
                                    .map(area => (
                                        <div
                                            key={area.id}
                                            className="link-item"
                                            onClick={() => {
                                                onLinkRecord('promotionalArea', area.id);
                                                onSearchChange('');
                                            }}
                                        >
                                            <strong>{area.name}</strong>
                                            <span>{area.locationDescription || 'No description'}</span>
                                        </div>
                                    ))}
                                {promotionalAreas.length === 0 && (
                                    <p className="empty-msg">No promotional areas found.</p>
                                )}
                            </div>
                        </>
                    )}

                    {/* Media Spaces */}
                    {selectedElement?.elementType === 'MediaSpace' && (
                        <>
                            <h3>Media Spaces</h3>
                            <div className="link-list">
                                {mediaSpaces
                                    .filter(space => !searchTerm ||
                                        (space.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        (space.locationDescription || '').toLowerCase().includes(searchTerm.toLowerCase())
                                    )
                                    .map(space => (
                                        <div
                                            key={space.id}
                                            className="link-item"
                                            onClick={() => {
                                                onLinkRecord('mediaSpace', space.id);
                                                onSearchChange('');
                                            }}
                                        >
                                            <strong>{space.name}</strong>
                                            <span>{space.locationDescription || 'No description'}</span>
                                        </div>
                                    ))}
                                {mediaSpaces.length === 0 && (
                                    <p className="empty-msg">No media spaces found.</p>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Keyboard Shortcut Help Modal ──────────────────────────────────────────────

/**
 * Read-only modal listing all keyboard shortcuts available in the designer.
 *
 * @param {Object}   props
 * @param {Function} props.onClose - Called when the modal should be dismissed.
 */
export const ShortcutHelpModal = ({ onClose }) => (
    <div className="fp-modal-overlay" onClick={onClose}>
        <div className="fp-modal" onClick={e => e.stopPropagation()}>
            <div className="fp-modal-header">
                <h2><MdKeyboard /> Keyboard Shortcuts</h2>
                <button className="btn-icon" onClick={onClose}><MdClose /></button>
            </div>
            <div className="fp-modal-body shortcut-help">
                <div className="shortcut-row"><kbd>V</kbd> <span>Select / Move tool</span></div>
                <div className="shortcut-row"><kbd>H</kbd> <span>Pan tool</span></div>
                <div className="shortcut-row"><kbd>G</kbd> <span>Toggle grid</span></div>
                <div className="shortcut-row"><kbd>Ctrl</kbd> + <kbd>S</kbd> <span>Save floorplan</span></div>
                <div className="shortcut-row"><kbd>Ctrl</kbd> + <kbd>Z</kbd> <span>Undo</span></div>
                <div className="shortcut-row"><kbd>Ctrl</kbd> + <kbd>Y</kbd> <span>Redo</span></div>
                <div className="shortcut-row"><kbd>Delete</kbd> <span>Delete selected element</span></div>
                <div className="shortcut-row"><kbd>Escape</kbd> <span>Cancel drawing / switch to Select</span></div>
                <div className="shortcut-row"><kbd>?</kbd> <span>Toggle this help panel</span></div>
                <div className="shortcut-row"><kbd>Scroll</kbd> <span>Zoom in / out</span></div>
                <div className="shortcut-row"><kbd>Double-click</kbd> <span>Finish drawing polygon</span></div>
                <div className="shortcut-row"><kbd>Right-click</kbd> <span>Context menu on element</span></div>
            </div>
        </div>
    </div>
);

// ── Element Type Config Modal ─────────────────────────────────────────────────

/**
 * Modal for configuring per-element-type display settings (icon, colour, size).
 * Allows users to customise how each element type appears on the floorplan
 * instead of relying on hardcoded values.
 *
 * @param {Object}   props
 * @param {Array}    props.configs          - Current element type configs from the server.
 * @param {Array}    props.elementTypes     - List of all available element type keys.
 * @param {Function} props.onSave           - Called with (elementType, configData) when saving.
 * @param {Function} props.onClose          - Called when the modal should be dismissed.
 */
export const ElementTypeConfigModal = ({ configs, elementTypes, onSave, onClose }) => {
    const [selectedType, setSelectedType] = useState(elementTypes[0] || '');
    const currentConfig = configs.find(c => c.elementType === selectedType) || {};
    const [form, setForm] = useState({
        iconEmoji: currentConfig.iconEmoji || '',
        color: currentConfig.color || '#333333',
        backgroundColor: currentConfig.backgroundColor || '#cccccc',
        defaultWidth: currentConfig.defaultWidth || 40,
        defaultHeight: currentConfig.defaultHeight || 40
    });

    const handleTypeChange = (type) => {
        setSelectedType(type);
        const cfg = configs.find(c => c.elementType === type) || {};
        setForm({
            iconEmoji: cfg.iconEmoji || '',
            color: cfg.color || '#333333',
            backgroundColor: cfg.backgroundColor || '#cccccc',
            defaultWidth: cfg.defaultWidth || 40,
            defaultHeight: cfg.defaultHeight || 40
        });
    };

    return (
        <div className="fp-modal-overlay" onClick={onClose}>
            <div className="fp-modal fp-modal-wide" onClick={e => e.stopPropagation()}>
                <div className="fp-modal-header">
                    <h2>⚙️ Element Type Appearance</h2>
                    <button className="btn-icon" onClick={onClose}><MdClose /></button>
                </div>
                <div className="fp-modal-body">
                    <p className="config-hint">Customise how each element type looks on the floorplan. Changes apply to this property only.</p>
                    <div className="config-layout">
                        <div className="config-type-list">
                            {elementTypes.map(type => (
                                <button
                                    key={type}
                                    className={`config-type-item ${selectedType === type ? 'active' : ''}`}
                                    onClick={() => handleTypeChange(type)}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                        <div className="config-form">
                            <h3>{selectedType}</h3>
                            <label>
                                Icon (emoji):
                                <input
                                    type="text"
                                    maxLength={4}
                                    value={form.iconEmoji}
                                    onChange={e => setForm({ ...form, iconEmoji: e.target.value })}
                                    placeholder="e.g. 🧯 📢 ⚡"
                                />
                                <small>Single emoji character displayed on the canvas.</small>
                            </label>
                            <label>
                                Colour:
                                <input
                                    type="color"
                                    value={form.color}
                                    onChange={e => setForm({ ...form, color: e.target.value })}
                                />
                                <small>Primary stroke / text colour.</small>
                            </label>
                            <label>
                                Background:
                                <input
                                    type="color"
                                    value={form.backgroundColor}
                                    onChange={e => setForm({ ...form, backgroundColor: e.target.value })}
                                />
                                <small>Fill / background colour on the canvas.</small>
                            </label>
                            <label>
                                Default Width (px):
                                <input
                                    type="number"
                                    min="10"
                                    max="200"
                                    value={form.defaultWidth}
                                    onChange={e => setForm({ ...form, defaultWidth: parseInt(e.target.value) || 40 })}
                                />
                            </label>
                            <label>
                                Default Height (px):
                                <input
                                    type="number"
                                    min="10"
                                    max="200"
                                    value={form.defaultHeight}
                                    onChange={e => setForm({ ...form, defaultHeight: parseInt(e.target.value) || 40 })}
                                />
                            </label>
                            <div className="config-preview">
                                <span>Preview:</span>
                                <div
                                    className="config-preview-box"
                                    style={{
                                        width: Math.min(form.defaultWidth, 80),
                                        height: Math.min(form.defaultHeight, 80),
                                        backgroundColor: form.backgroundColor + '30',
                                        border: `2px solid ${form.color}`,
                                        borderRadius: 4,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 20
                                    }}
                                >
                                    {form.iconEmoji || '?'}
                                </div>
                            </div>
                            <button className="btn-primary" onClick={() => onSave(selectedType, form)}>
                                <MdSave /> Save {selectedType}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
